/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/callFlowApi.ts
 🗓️ Created: 2025-01-28
 🧩 Description:
    API functions for Call Flows.
    Centralizes all API calls related to Call Flows.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import {
  CallFlow,
  CallFlowPayload,
  ListCallFlowsResponse,
  ToggleCallFlowPayload,
  ToggleCallFlowResponse,
} from '../types';

export const CALL_FLOWS_ENDPOINT = '/api/telephony/call-flows';

/**
 * Generic API request helper
 */
async function requestCallFlowsApi<T>(
  endpoint: string,
  errorMessage: string,
  options?: RequestInit,
): Promise<{ data: T }> {
  const response = await fetch(buildApiUrl(endpoint), {
    ...options,
    headers: {
      ...getApiHeaders(),
      ...(options?.headers || {}),
    },
  });

  if (!response.ok) {
    let message = errorMessage;
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(message);
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || errorMessage);
  }

  return { data: result.data };
}

/**
 * List Call Flows with pagination and search
 */
export async function fetchCallFlows(
  pagination: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    domainId?: number;
  },
  search?: string,
  sortBy?: string,
): Promise<ListCallFlowsResponse> {
  const queryParams = new URLSearchParams({
    page: pagination.page.toString(),
    limit: pagination.limit.toString(),
    sortBy: sortBy || pagination.sortBy,
    sortOrder: pagination.sortOrder,
  });

  if (search) {
    queryParams.append('search', search);
  }

  if (pagination.domainId != null && !Number.isNaN(Number(pagination.domainId))) {
    queryParams.append('domainId', pagination.domainId.toString());
  }

  const response = await fetch(
    buildApiUrl(`${CALL_FLOWS_ENDPOINT}?${queryParams}`),
    {
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch call flows');
  }

  const result = await response.json();

  if (!result?.success || !Array.isArray(result.data)) {
    throw new Error(result?.message || 'Invalid response');
  }

  return {
    success: true,
    data: result.data as CallFlow[],
    pagination: {
      page: result.pagination?.page || pagination.page,
      limit: result.pagination?.limit || pagination.limit,
      total: result.pagination?.total || 0,
      totalPages: result.pagination?.totalPages || 0,
    },
  };
}

/**
 * Get single Call Flow by ID
 */
export async function fetchCallFlow(id: string | number): Promise<CallFlow> {
  const { data } = await requestCallFlowsApi<CallFlow>(
    `${CALL_FLOWS_ENDPOINT}/${id}`,
    'Failed to fetch call flow',
  );
  return data;
}

/**
 * Create new Call Flow
 */
export async function createCallFlow(
  payload: CallFlowPayload,
): Promise<CallFlow> {
  const { data } = await requestCallFlowsApi<CallFlow>(
    CALL_FLOWS_ENDPOINT,
    'Failed to create call flow',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  return data;
}

/**
 * Update Call Flow
 */
export async function updateCallFlow(
  id: string | number,
  payload: CallFlowPayload,
): Promise<CallFlow> {
  const { data } = await requestCallFlowsApi<CallFlow>(
    `${CALL_FLOWS_ENDPOINT}/${id}`,
    'Failed to update call flow',
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  return data;
}

/**
 * Toggle Call Flow status
 */
export async function toggleCallFlow(
  id: string | number,
  payload: ToggleCallFlowPayload,
): Promise<ToggleCallFlowResponse> {
  const response = await fetch(
    buildApiUrl(`${CALL_FLOWS_ENDPOINT}/${id}/toggle`),
    {
      method: 'PUT',
      headers: {
        ...getApiHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    let message = 'Failed to toggle call flow';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(message);
  }

  const result = await response.json();
  if (!result?.success) {
    throw new Error(result?.message || 'Failed to toggle call flow');
  }

  return result as ToggleCallFlowResponse;
}

/**
 * Delete Call Flow
 */
export async function deleteCallFlow(id: string | number): Promise<void> {
  await requestCallFlowsApi<null>(
    `${CALL_FLOWS_ENDPOINT}/${id}`,
    'Failed to delete call flow',
    {
      method: 'DELETE',
    },
  );
}

